import {Suspense, useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import styled from 'styled-components';
import type {SettingsCustomFieldsTabQuery as SettingsCustomFieldsTabQueryType} from './__generated__/SettingsCustomFieldsTabQuery.graphql';
import {useCreateCustomFieldDefinition} from '../hooks/useCreateCustomFieldDefinition';
import {useDeleteCustomFieldDefinition} from '../hooks/useDeleteCustomFieldDefinition';
import {useUpdateCustomFieldDefinition} from '../hooks/useUpdateCustomFieldDefinition';

const SettingsCustomFieldsTabQueryDoc = graphql`
	query SettingsCustomFieldsTabQuery {
		viewer {
			customFieldDefinitions {
				edges {
					node {
						id
						name
						key
						fieldType
						options
						readOnly
						requiredOnResolution
					}
				}
			}
		}
	}
`;

const OptionsEditorContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled.span`
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #334155;
`;

const InputRow = styled.div`
	display: flex;
	gap: 8px;
`;

const OptionInput = styled.input`
	flex: 1;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const SecondaryButton = styled.button`
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	color: #334155;
	&:hover {
		background: #f8fafc;
	}
	&:disabled {
		opacity: 0.5;
	}
`;

const EmptyHint = styled.p`
	font-size: 12px;
	line-height: 16px;
	color: #64748b;
`;

const OptionsList = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const OptionItem = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: 4px;
	background: #f8fafc;
	padding: 4px 8px;
	font-size: 14px;
	line-height: 20px;
	color: #334155;
`;

const RemoveOptionButton = styled.button`
	border-radius: 4px;
	padding-left: 6px;
	padding-right: 6px;
	color: #64748b;
	&:hover {
		background: #e2e8f0;
		color: #334155;
	}
`;

interface CustomFieldRow {
	id: string;
	name: string;
	key: string;
	fieldType: 'FREE_TEXT' | 'DROPDOWN' | '%future added value';
	options: readonly string[] | null | undefined;
	readOnly: boolean;
	requiredOnResolution: boolean;
}

interface OptionsEditorProps {
	options: string[];
	onChange: (next: string[]) => void;
}

function OptionsEditor({options, onChange}: OptionsEditorProps) {
	const [draft, setDraft] = useState('');

	function addOption() {
		const trimmed = draft.trim();
		if (trimmed.length === 0) return;
		if (options.includes(trimmed)) {
			setDraft('');
			return;
		}
		onChange([...options, trimmed]);
		setDraft('');
	}

	function removeOption(value: string) {
		onChange(options.filter(o => o !== value));
	}

	return (
		<OptionsEditorContainer>
			<FieldLabel>Options</FieldLabel>
			<InputRow>
				<OptionInput
					type="text"
					value={draft}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							addOption();
						}
					}}
					placeholder="Add an option"
					aria-label="New option"
				/>
				<SecondaryButton type="button" onClick={addOption} disabled={draft.trim().length === 0}>
					Add
				</SecondaryButton>
			</InputRow>
			{options.length === 0 ? (
				<EmptyHint>No options yet. Add at least one.</EmptyHint>
			) : (
				<OptionsList>
					{options.map(opt => (
						<OptionItem key={opt}>
							<span>{opt}</span>
							<RemoveOptionButton type="button" onClick={() => removeOption(opt)} aria-label={`Remove ${opt}`}>
								×
							</RemoveOptionButton>
						</OptionItem>
					))}
				</OptionsList>
			)}
		</OptionsEditorContainer>
	);
}

function fieldTypeLabel(fieldType: CustomFieldRow['fieldType']): string {
	if (fieldType === 'FREE_TEXT') return 'Free Text';
	if (fieldType === 'DROPDOWN') return 'Dropdown';
	return fieldType;
}

function slugify(value: string): string {
	return value
		.toLowerCase()
		.replace(/\s+/g, '_')
		.replace(/[^a-z0-9_]/g, '');
}

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgb(0 0 0 / 0.4);
	padding: 16px;
`;

const ModalCard = styled.div`
	width: 100%;
	max-width: 28rem;
	border-radius: 8px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
`;

const ModalForm = styled.form`
	width: 100%;
	max-width: 28rem;
	border-radius: 8px;
	background: #ffffff;
	padding: 24px;
	box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
`;

const ModalTitle = styled.h2`
	font-size: 18px;
	line-height: 28px;
	font-weight: 600;
	color: #0f172a;
`;

const DialogMessage = styled.p`
	margin-top: 12px;
	font-size: 14px;
	line-height: 20px;
	color: #475569;
`;

const ErrorText = styled.p`
	margin-top: 12px;
	font-size: 14px;
	line-height: 20px;
	color: #dc2626;
`;

const InlineErrorText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #dc2626;
`;

const ModalActions = styled.div`
	margin-top: 24px;
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

const DangerButton = styled.button`
	border-radius: 4px;
	background: #dc2626;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	&:hover {
		background: #b91c1c;
	}
	&:disabled {
		opacity: 0.5;
	}
`;

const PrimaryButton = styled.button`
	border-radius: 4px;
	background: #2563eb;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	&:hover {
		background: #1d4ed8;
	}
	&:disabled {
		opacity: 0.5;
	}
`;

const ModalFields = styled.div`
	margin-top: 16px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const BlockLabel = styled.label`
	display: block;
`;

const TextInput = styled.input`
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const MonoTextInput = styled.input`
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 6px 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const TypeSelect = styled.select`
	margin-top: 4px;
	width: 100%;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 6px 8px;
	font-size: 14px;
	line-height: 20px;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Checkbox = styled.input`
	height: 16px;
	width: 16px;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
`;

const CheckboxText = styled.span`
	font-size: 14px;
	line-height: 20px;
	color: #334155;
`;

const TypeFieldBlock = styled.div`
	display: block;
`;

const TypeBadgeRow = styled.div`
	margin-top: 4px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const TypeBadge = styled.span`
	display: inline-flex;
	align-items: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #f1f5f9;
	padding: 2px 10px;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	color: #334155;
`;

const TypeBadgeHint = styled.span`
	font-size: 12px;
	line-height: 16px;
	color: #64748b;
`;

interface ConfirmDialogProps {
	title: string;
	message: string;
	confirmLabel?: string;
	loading?: boolean;
	error?: string | null;
	onConfirm: () => void;
	onCancel: () => void;
}

function ConfirmDialog({title, message, confirmLabel = 'Delete', loading, error, onConfirm, onCancel}: ConfirmDialogProps) {
	return (
		<ModalOverlay role="dialog" aria-label={title} onClick={onCancel}>
			<ModalCard onClick={e => e.stopPropagation()}>
				<ModalTitle>{title}</ModalTitle>
				<DialogMessage>{message}</DialogMessage>
				{error && <ErrorText>{error}</ErrorText>}
				<ModalActions>
					<SecondaryButton type="button" onClick={onCancel}>
						Cancel
					</SecondaryButton>
					<DangerButton type="button" onClick={onConfirm} disabled={loading}>
						{loading ? 'Deleting…' : confirmLabel}
					</DangerButton>
				</ModalActions>
			</ModalCard>
		</ModalOverlay>
	);
}

interface CreateCustomFieldModalProps {
	onClose: () => void;
	onCreated: () => void;
}

function CreateCustomFieldModal({onClose, onCreated}: CreateCustomFieldModalProps) {
	const [name, setName] = useState('');
	const [key, setKey] = useState('');
	const [keyTouched, setKeyTouched] = useState(false);
	const [fieldType, setFieldType] = useState<'FREE_TEXT' | 'DROPDOWN'>('FREE_TEXT');
	const [options, setOptions] = useState<string[]>([]);
	const [readOnly, setReadOnly] = useState(false);
	const [requiredOnResolution, setRequiredOnResolution] = useState(false);
	const {create, loading, error, setError} = useCreateCustomFieldDefinition();

	const trimmedName = name.trim();
	const trimmedKey = key.trim();
	const optionsValid = fieldType !== 'DROPDOWN' || options.length > 0;
	const canSubmit = trimmedName.length > 0 && trimmedKey.length > 0 && optionsValid && !loading;

	function handleNameChange(value: string) {
		setName(value);
		if (!keyTouched) {
			setKey(slugify(value));
		}
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;
		setError(null);
		try {
			await create({
				name: trimmedName,
				key: trimmedKey,
				fieldType,
				options: fieldType === 'DROPDOWN' ? options : undefined,
				readOnly,
				requiredOnResolution,
			});
			onCreated();
			onClose();
		} catch {
			// error set by hook
		}
	}

	return (
		<ModalOverlay role="dialog" aria-label="New Custom Field" onClick={onClose}>
			<ModalForm onClick={e => e.stopPropagation()} onSubmit={e => void handleSubmit(e)}>
				<ModalTitle>New Custom Field</ModalTitle>
				<ModalFields>
					<BlockLabel>
						<FieldLabel>Display Name</FieldLabel>
						<TextInput
							type="text"
							value={name}
							onChange={e => handleNameChange(e.target.value)}
							placeholder="e.g. Customer Priority"
							autoFocus
						/>
					</BlockLabel>
					<BlockLabel>
						<FieldLabel>Key</FieldLabel>
						<MonoTextInput
							type="text"
							value={key}
							onChange={e => {
								setKeyTouched(true);
								setKey(e.target.value);
							}}
							placeholder="e.g. customer_priority"
						/>
					</BlockLabel>
					<BlockLabel>
						<FieldLabel>Type</FieldLabel>
						<TypeSelect value={fieldType} onChange={e => setFieldType(e.target.value as 'FREE_TEXT' | 'DROPDOWN')}>
							<option value="FREE_TEXT">Free Text</option>
							<option value="DROPDOWN">Dropdown</option>
						</TypeSelect>
					</BlockLabel>
					{fieldType === 'DROPDOWN' && <OptionsEditor options={options} onChange={setOptions} />}
					<CheckboxLabel>
						<Checkbox type="checkbox" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} />
						<CheckboxText>Read-only</CheckboxText>
					</CheckboxLabel>
					<CheckboxLabel>
						<Checkbox
							type="checkbox"
							checked={requiredOnResolution}
							onChange={e => setRequiredOnResolution(e.target.checked)}
						/>
						<CheckboxText>Required on Resolution</CheckboxText>
					</CheckboxLabel>
					{error && <InlineErrorText>{error}</InlineErrorText>}
				</ModalFields>
				<ModalActions>
					<SecondaryButton type="button" onClick={onClose}>
						Cancel
					</SecondaryButton>
					<PrimaryButton type="submit" disabled={!canSubmit}>
						{loading ? 'Creating…' : 'Create Custom Field'}
					</PrimaryButton>
				</ModalActions>
			</ModalForm>
		</ModalOverlay>
	);
}

interface EditCustomFieldModalProps {
	field: CustomFieldRow;
	onClose: () => void;
	onUpdated: () => void;
}

function EditCustomFieldModal({field, onClose, onUpdated}: EditCustomFieldModalProps) {
	const isDropdown = field.fieldType === 'DROPDOWN';
	const [name, setName] = useState(field.name);
	const [options, setOptions] = useState<string[]>(field.options ? [...field.options] : []);
	const [readOnly, setReadOnly] = useState(field.readOnly);
	const [requiredOnResolution, setRequiredOnResolution] = useState(field.requiredOnResolution);
	const {update, loading, error, setError} = useUpdateCustomFieldDefinition();

	const trimmedName = name.trim();
	const optionsValid = !isDropdown || options.length > 0;
	const canSubmit = trimmedName.length > 0 && optionsValid && !loading;

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;
		setError(null);
		try {
			await update({
				id: field.id,
				name: trimmedName,
				options: isDropdown ? options : undefined,
				readOnly,
				requiredOnResolution,
			});
			onUpdated();
			onClose();
		} catch {
			// error set by hook
		}
	}

	return (
		<ModalOverlay role="dialog" aria-label="Edit Custom Field" onClick={onClose}>
			<ModalForm onClick={e => e.stopPropagation()} onSubmit={e => void handleSubmit(e)}>
				<ModalTitle>Edit Custom Field</ModalTitle>
				<ModalFields>
					<BlockLabel>
						<FieldLabel>Display Name</FieldLabel>
						<TextInput type="text" value={name} onChange={e => setName(e.target.value)} autoFocus />
					</BlockLabel>
					<TypeFieldBlock>
						<FieldLabel>Type</FieldLabel>
						<TypeBadgeRow>
							<TypeBadge title="Cannot be changed">{fieldTypeLabel(field.fieldType)}</TypeBadge>
							<TypeBadgeHint>Cannot be changed</TypeBadgeHint>
						</TypeBadgeRow>
					</TypeFieldBlock>
					{isDropdown && <OptionsEditor options={options} onChange={setOptions} />}
					<CheckboxLabel>
						<Checkbox type="checkbox" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} />
						<CheckboxText>Read-only</CheckboxText>
					</CheckboxLabel>
					<CheckboxLabel>
						<Checkbox
							type="checkbox"
							checked={requiredOnResolution}
							onChange={e => setRequiredOnResolution(e.target.checked)}
						/>
						<CheckboxText>Required on Resolution</CheckboxText>
					</CheckboxLabel>
					{error && <InlineErrorText>{error}</InlineErrorText>}
				</ModalFields>
				<ModalActions>
					<SecondaryButton type="button" onClick={onClose}>
						Cancel
					</SecondaryButton>
					<PrimaryButton type="submit" disabled={!canSubmit}>
						{loading ? 'Saving…' : 'Save changes'}
					</PrimaryButton>
				</ModalActions>
			</ModalForm>
		</ModalOverlay>
	);
}

interface CustomFieldsTabContentProps {
	fetchKey: number;
	bumpFetchKey: () => void;
}

const TabHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const TabTitle = styled.h2`
	font-size: 16px;
	line-height: 24px;
	font-weight: 600;
	color: #1e293b;
`;

const AddButton = styled.button`
	border-radius: 4px;
	background: #2563eb;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	&:hover {
		background: #1d4ed8;
	}
`;

const EmptyState = styled.p`
	margin-top: 16px;
	font-size: 14px;
	line-height: 20px;
	color: #64748b;
`;

const TableScroll = styled.div`
	margin-top: 16px;
	overflow-x: auto;
`;

const FieldsTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: 14px;
	line-height: 20px;
`;

const HeaderRow = styled.tr`
	border-bottom: 1px solid #e2e8f0;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;

const HeaderCell = styled.th`
	padding-top: 8px;
	padding-bottom: 8px;
	padding-right: 16px;
	text-align: left;
`;

const HeaderCellNoPad = styled.th`
	padding-top: 8px;
	padding-bottom: 8px;
	text-align: left;
`;

const BodyRow = styled.tr`
	border-bottom: 1px solid #f1f5f9;
`;

const NameCell = styled.td`
	padding-top: 8px;
	padding-bottom: 8px;
	padding-right: 16px;
	color: #1e293b;
`;

const MonoCell = styled.td`
	padding-top: 8px;
	padding-bottom: 8px;
	padding-right: 16px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	color: #475569;
`;

const Cell = styled.td`
	padding-top: 8px;
	padding-bottom: 8px;
	padding-right: 16px;
	color: #475569;
`;

const ActionsCell = styled.td`
	padding-top: 8px;
	padding-bottom: 8px;
`;

const ActionsRow = styled.div`
	display: flex;
	gap: 8px;
`;

const RowActionButton = styled.button`
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 2px 8px;
	font-size: 12px;
	line-height: 16px;
	color: #475569;
	&:hover {
		background: #f8fafc;
	}
`;

function CustomFieldsTabContent({fetchKey, bumpFetchKey}: CustomFieldsTabContentProps) {
	const data = useLazyLoadQuery<SettingsCustomFieldsTabQueryType>(
		SettingsCustomFieldsTabQueryDoc,
		{},
		{fetchKey, fetchPolicy: 'network-only'}
	);

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [fieldToDelete, setFieldToDelete] = useState<{id: string; name: string} | null>(null);
	const [fieldToEdit, setFieldToEdit] = useState<CustomFieldRow | null>(null);

	const {remove, loading: deleting, error: deleteError, setError: setDeleteError} = useDeleteCustomFieldDefinition();

	const fields: CustomFieldRow[] = (data.viewer?.customFieldDefinitions?.edges ?? []).flatMap(e => (e?.node ? [e.node] : []));

	async function confirmDelete() {
		if (!fieldToDelete) return;
		setDeleteError(null);
		try {
			await remove(fieldToDelete.id);
			setFieldToDelete(null);
			bumpFetchKey();
		} catch {
			// error set by hook
		}
	}

	return (
		<div>
			<TabHeader>
				<TabTitle>Custom Fields</TabTitle>
				<AddButton type="button" onClick={() => setShowCreateModal(true)}>
					+ Add custom field
				</AddButton>
			</TabHeader>

			{fields.length === 0 ? (
				<EmptyState>No custom fields yet. Add one to get started.</EmptyState>
			) : (
				<TableScroll>
					<FieldsTable>
						<thead>
							<HeaderRow>
								<HeaderCell>Display Name</HeaderCell>
								<HeaderCell>Key</HeaderCell>
								<HeaderCell>Type</HeaderCell>
								<HeaderCell>Read-only</HeaderCell>
								<HeaderCell>Required on Resolution</HeaderCell>
								<HeaderCellNoPad></HeaderCellNoPad>
							</HeaderRow>
						</thead>
						<tbody>
							{fields.map(field => (
								<BodyRow key={field.id}>
									<NameCell>{field.name}</NameCell>
									<MonoCell>{field.key}</MonoCell>
									<Cell>{fieldTypeLabel(field.fieldType)}</Cell>
									<Cell>{field.readOnly ? 'Yes' : 'No'}</Cell>
									<Cell>{field.requiredOnResolution ? 'Yes' : 'No'}</Cell>
									<ActionsCell>
										<ActionsRow>
											<RowActionButton onClick={() => setFieldToEdit(field)}>Edit</RowActionButton>
											<RowActionButton onClick={() => setFieldToDelete({id: field.id, name: field.name})}>
												Delete
											</RowActionButton>
										</ActionsRow>
									</ActionsCell>
								</BodyRow>
							))}
						</tbody>
					</FieldsTable>
				</TableScroll>
			)}

			{showCreateModal && <CreateCustomFieldModal onClose={() => setShowCreateModal(false)} onCreated={bumpFetchKey} />}
			{fieldToDelete && (
				<ConfirmDialog
					title={`Delete "${fieldToDelete.name}"?`}
					message="Deleting this field will permanently remove all its values from every ticket."
					loading={deleting}
					error={deleteError}
					onConfirm={() => void confirmDelete()}
					onCancel={() => {
						setFieldToDelete(null);
						setDeleteError(null);
					}}
				/>
			)}
			{fieldToEdit && (
				<EditCustomFieldModal field={fieldToEdit} onClose={() => setFieldToEdit(null)} onUpdated={bumpFetchKey} />
			)}
		</div>
	);
}

const LoadingText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #64748b;
`;

export function SettingsCustomFieldsTab() {
	const [fetchKey, setFetchKey] = useState(0);
	const bumpFetchKey = () => setFetchKey(k => k + 1);

	return (
		<Suspense fallback={<LoadingText>Loading…</LoadingText>}>
			<CustomFieldsTabContent fetchKey={fetchKey} bumpFetchKey={bumpFetchKey} />
		</Suspense>
	);
}
